import { ListStyle } from '@yleisradio/areena-types';
import getConfig from 'next/config';
import { Consent } from 'types/yleConsentSdk';
import { snowplowAnalyticsProperties } from '../properties/frontend';
import {
  clickEventGenerator,
  viewEventGenerator,
  elementContextGenerator,
  entityContextGenerator,
  GenericContext,
  groupContextGeneratorForHighlightCard,
  groupContextGeneratorForList,
  imageContextGenerator,
  wrapperContextGenerator,
  yleConsentGenerator,
} from './contextGenerators';

export const initSnowplowTracker = (): void => {
  const {
    publicRuntimeConfig: { versionNumber },
  } = getConfig();
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  const { appId, collectorUrl, mediaPluginUrl, trackerNamespace } =
    snowplowAnalyticsProperties;

  window.yleSnowplow('newTracker', trackerNamespace, collectorUrl, {
    appId: appId,
    appVersion: versionNumber,
    discoverRootDomain: true,
    cookieSameSite: 'Lax',
    contexts: {
      browser: true,
      session: true,
      webPage: true,
    },
    platform: 'web',
  });

  window.yleSnowplow('addPlugin', mediaPluginUrl, [
    'snowplowMedia',
    'SnowplowMediaPlugin',
  ]);
};

const replaceYleContext = (context: GenericContext) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  window.yleSnowplow('addGlobalContexts', { yle_context: context });
};

export const addConsentContext = (consent: Consent) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  const contexts = [yleConsentGenerator(consent)];
  window.yleSnowplow('addGlobalContexts', contexts);
};

export const clearUser = () => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  window.yleSnowplow('clearUserData');
  window.yleSnowplow('newSession');
};

export const setUser = (transientId: string) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  window.yleSnowplow('setUserId', transientId);
};

export const trackPageView = (yleContext: GenericContext) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  replaceYleContext(yleContext);

  window.yleSnowplow('trackPageView');
};

export const trackCardView = ({
  entityId,
  cardIndex,
  cardTitle,
  cardViewUuid,
  imageId,
  isListPaginated,
  listIndex,
  listLabel,
  listLength,
  listReferer,
  listUrl,
  listStyle,
}: TrackCardViewArgs) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  const event = viewEventGenerator(cardViewUuid, 'entity');
  const context = [
    entityContextGenerator(entityId),
    wrapperContextGenerator(),
    groupContextGeneratorForList(
      isListPaginated,
      listLabel,
      listLength,
      listReferer,
      listStyle,
      listUrl
    ),
    elementContextGenerator(cardIndex, listIndex, listStyle, cardTitle, 'card'),
    ...(imageId ? [imageContextGenerator(imageId)] : []),
  ];

  window.yleSnowplow('trackSelfDescribingEvent', { event, context });
};

export const trackHighlightCardView = ({
  viewUuid,
  contentIndex,
  imageId,
  title,
}: TrackHighlightCardViewArgs) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  const event = viewEventGenerator(viewUuid, 'ui_element');
  const context = [
    wrapperContextGenerator(),
    groupContextGeneratorForHighlightCard(title),
    elementContextGenerator(
      0,
      contentIndex,
      undefined,
      title,
      'highlight_card'
    ),
    ...(imageId ? [imageContextGenerator(imageId)] : []),
  ];

  window.yleSnowplow('trackSelfDescribingEvent', { event, context });
};

export const trackClick = ({ action, viewUuid }: TrackClickArgs) => {
  if (!window.yleSnowplow) {
    throw new Error('Snowplow tracker is not available');
  }

  const event = clickEventGenerator(action, viewUuid);

  window.yleSnowplow('trackSelfDescribingEvent', { event });
};

export const getListContext = ({
  isPaginated = false,
  label,
  length = null,
  referer,
  style,
  url,
}: GetListContextArgs) => {
  return [
    wrapperContextGenerator(),
    groupContextGeneratorForList(
      isPaginated,
      label,
      length,
      referer,
      style,
      url
    ),
  ];
};

export type GetListContextArgs = {
  isPaginated?: boolean;
  label: string | undefined;
  length?: number | null;
  referer: string | undefined;
  style: ListStyle | undefined;
  url: string | undefined;
};

export type TrackCardViewArgs = {
  cardIndex: number;
  cardTitle: string | undefined;
  cardViewUuid: string;
  entityId: string;
  imageId: string | undefined;
  isListPaginated: boolean;
  listIndex: number;
  listLabel: string | undefined;
  listLength: number | null;
  listReferer: string | undefined;
  listStyle: ListStyle | undefined;
  listUrl: string | undefined;
};

export type TrackHighlightCardViewArgs = {
  title: string | undefined;
  viewUuid: string;
  imageId: string | undefined;
  contentIndex: number;
};

export type TrackClickArgs = {
  action: 'link' | 'play';
  viewUuid: string;
};
